import TrialLock from './TrialLock';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaChevronDown, FaChevronRight, FaTimes } from 'react-icons/fa';
import { flushSync } from 'react-dom';
import { AppDispatch, RootState } from '../store/store';
import { logout } from '../store/slices/authSlice';
import { toggleTheme } from '../store/slices/uiSlice';
import { getKeycloakInstance } from '../utility/keycloak';
import { isAdmin } from '../utility/isAdmin';
import { resetInstances } from '../store/slices/instancesSlice';
import { resetImages } from '../store/slices/imagesSlice';
import { resetFlavors } from '../store/slices/flavorsSlice';
import { resetOverview } from '../store/slices/overviewSlice';
import '../styles/Navbar.scss';

type NavigationItem = { label: string; to: string };
type NavigationSection = { label: string; items: NavigationItem[] };

// The free tier's own page: what is included and what to ask the agent (Milos, 2026-11-02: "It should just have a
// dedicated page with explanation"). The free VMs themselves are rows in the instances table's Free tier tab.
export const FREE_TIER_LINK = '/free-tier';

const navigationSections: NavigationSection[] = [
    {
        label: 'Start',
        items: [
            { label: 'Overview', to: '/category/compute/overview' },
            { label: 'Deploy', to: '/deploy' },
            { label: 'MCP setup', to: '/mcp' },
            { label: 'Free tier', to: FREE_TIER_LINK },
        ],
    },
    { label: 'Compute', items: [{ label: 'Virtual machines', to: '/category/compute/overview' }, { label: 'Clusters', to: '/category/compute/clusters' }] },
    { label: 'Network', items: [{ label: 'Private networks', to: '/category/network/private-networks' }, { label: 'Security groups', to: '/category/network/security-groups' }, { label: 'Load balancers', to: '/category/network/load-balancers' }] },
    { label: 'Block storage', items: [{ label: 'Volumes', to: '/category/block-storage/volumes' }] },
    { label: 'Managed', items: [{ label: 'Databases', to: '/category/managed/databases' }, { label: 'MySQL', to: '/category/managed/mysql' }, { label: 'ClickHouse', to: '/category/managed/clickhouse' }, { label: 'Redis', to: '/category/managed/redis' }, { label: 'Vector DB', to: '/category/managed/vector-db' }] },
];

const Navbar: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const userInfo = useSelector((state: RootState) => state.auth.userInfo);
    const theme = useSelector((state: RootState) => state.ui.theme);
    const regions = useSelector((state: RootState) => state.regions);
    const [isDrawerOpen, setDrawerOpen] = useState(false);
    const [isAccountOpen, setAccountOpen] = useState(false);
    const accountMenuRef = useRef<HTMLDivElement>(null);
    const [openSection, setOpenSection] = useState<string | null>('Start');
    const preferredUsername = userInfo?.preferred_username || 'Account';
    const accountInitial = preferredUsername.trim().charAt(0).toUpperCase() || 'A';
    const userIsAdmin = isAdmin(userInfo);
    // Start > Free tier is for accounts on the current plans, which all include the free VMs.
    const freeTierPlan = useSelector((state: RootState) => state.billingForecast.forecast?.policy_version === 3);

    useEffect(() => {
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setDrawerOpen(false);
                setAccountOpen(false);
            }
        };
        window.addEventListener('keydown', closeOnEscape);
        return () => window.removeEventListener('keydown', closeOnEscape);
    }, []);

    useEffect(() => {
        const closeOnOutsideClick = (event: MouseEvent) => {
            if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) setAccountOpen(false);
        };
        document.addEventListener('mousedown', closeOnOutsideClick);
        return () => document.removeEventListener('mousedown', closeOnOutsideClick);
    }, []);

    const navigateTo = (to: string) => {
        setDrawerOpen(false);
        setAccountOpen(false);
        navigate(to);
    };

    const handleLogout = async () => {
        const keycloak = getKeycloakInstance();
        try {
            dispatch(logout());
            flushSync(() => {
                dispatch(resetInstances());
                dispatch(resetImages());
                dispatch(resetFlavors());
                dispatch(resetOverview());
            });
            localStorage.removeItem('auth');
            if (keycloak) {
                await keycloak.logout({ redirectUri: window.location.origin });
            } else {
                window.location.reload();
            }
        } catch (error) {
            console.error('Logout failed:', error);
            window.location.reload();
        }
    };

    return (
        <>
            <nav className="navbar" aria-label="Console navigation">
                <button
                    type="button"
                    className="console-mark"
                    onClick={() => setDrawerOpen(true)}
                    aria-label="Open navigation"
                    aria-expanded={isDrawerOpen}
                >
                    <span className="logo-main">R</span>
                    <span className="logo-sub">edu Cloud</span>
                </button>

                <div className="account-menu" ref={accountMenuRef}>
                    <button
                        type="button"
                        className="account-menu__trigger"
                        aria-haspopup="menu"
                        aria-expanded={isAccountOpen}
                        aria-label="Open account menu"
                        onClick={() => setAccountOpen(open => !open)}
                    >
                        <span className="account-menu__avatar" aria-hidden="true">{accountInitial}</span>
                        <FaChevronDown aria-hidden="true" />
                    </button>
                    {isAccountOpen && <div className="account-menu__panel is-open" role="menu">
                        <span className="account-menu__region">{regions.status === 'loading' ? 'Loading region' : (regions.currentRegion || 'Region')}</span>
                        <button type="button" role="menuitem" onClick={() => navigateTo('/category/user/api-keys')}>API keys</button>
                        <button type="button" role="menuitem" onClick={() => navigateTo('/category/user/billing')}>Billing</button>
                        <button type="button" role="menuitem" onClick={() => navigateTo('/category/user/domains')}>Domains</button>
                        {userIsAdmin && <button type="button" role="menuitem" onClick={() => navigateTo('/admin')}>Admin</button>}
                        <button type="button" role="menuitem" onClick={() => { dispatch(toggleTheme()); setAccountOpen(false); }}>
                            {theme === 'dark' ? 'Use light mode' : 'Use dark mode'}
                        </button>
                        <button type="button" role="menuitem" className="account-menu__logout" onClick={handleLogout}>Log out</button>
                    </div>}
                </div>
            </nav>

            {isDrawerOpen && (
                <div className="console-drawer-layer">
                    <button type="button" className="console-drawer-backdrop" onClick={() => setDrawerOpen(false)} aria-label="Close navigation" />
                    <aside className="console-drawer" aria-label="Primary navigation">
                        <div className="console-drawer__header">
                            <Link to="/" className="console-drawer__brand" onClick={() => setDrawerOpen(false)}>
                                <span className="logo-main">R</span><span>edu Cloud</span>
                            </Link>
                            <button type="button" className="console-drawer__close" onClick={() => setDrawerOpen(false)} aria-label="Close navigation"><FaTimes /></button>
                        </div>

                        <div className="console-drawer__sections">
                            {navigationSections.map((section) => {
                                const expanded = openSection === section.label;
                                return (
                                    <section className="console-drawer__section" key={section.label}>
                                        <button
                                            type="button"
                                            className="console-drawer__section-trigger"
                                            onClick={() => setOpenSection(expanded ? null : section.label)}
                                            aria-expanded={expanded}
                                        >
                                            {section.label}
                                            {expanded ? <FaChevronDown aria-hidden="true" /> : <FaChevronRight aria-hidden="true" />}
                                        </button>
                                        {expanded && (
                                            <div className="console-drawer__subsections">
                                                {/* Only the deploy launcher creates paid resources. Every other entry stays reachable whatever the
                                                    billing state, so an account in billing trouble can still open, stop or delete what it runs. */}
                                                {section.items.filter((item) => item.to !== FREE_TIER_LINK || freeTierPlan).map((item) => item.to === '/deploy'
                                                    ? <TrialLock key={item.to} locked={false} paidResource><button type="button" onClick={() => navigateTo(item.to)}>{item.label}</button></TrialLock>
                                                    : <button key={item.to} type="button" onClick={() => navigateTo(item.to)}>{item.label}</button>
                                                )}
                                            </div>
                                        )}
                                    </section>
                                );
                            })}
                        </div>

                        <div className="console-drawer__footer">
                            <a href="https://docs.redu.cloud" target="_blank" rel="noopener noreferrer">Documentation</a>
                            <span>{regions.status === 'loading' ? 'Loading region' : (regions.currentRegion || 'Region')}</span>
                        </div>
                    </aside>
                </div>
            )}
        </>
    );
};

export default Navbar;
